import { useMutation, MutationFunction } from '@apollo/client';
import {
  AlertModal,
  Button,
  Icon,
  Stack,
  Table,
  TableColumn,
  Tooltip,
  useConfirmation,
  useNotifications,
} from '@sixfold/common-ui';
import { notNil, safeParseDate } from '@sixfold/typed-primitives';
import React from 'react';
import { NavLink } from 'react-router';

import { TimestampLink } from './timestamp_link';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useIsAdmin } from '../../lib/authorization';
import { compareAsc } from '../../lib/date';
import {
  DeleteExternalTourEventMutation,
  DeleteExternalStopEventMutation,
  DeleteExternalDeliveryEventMutation,
  DeleteExternalTourEventMutationVariables,
  DeleteExternalStopEventMutationVariables,
  DeleteExternalDeliveryEventMutationVariables,
} from '../../lib/graphql';
import { Routes } from '../../routes';
import { isTourExternalEvent, isTourExternalStopEvent, isTourExternalDeliveryEvent, ExternalEvent } from '../entities';
import {
  tourQuery,
  deleteExternalTourEventMutation,
  deleteExternalStopEventMutation,
  deleteExternalDeliveryEventMutation,
} from '../graphql';
import { breakWordAtChar } from '../utils/break_word_util';

export interface ExternalEventListProps {
  tourId: number;
  events: ExternalEvent[];
}

function parseDateWithDefault(dateStr: string | null, defaultDate: Date): Date {
  const date = safeParseDate(dateStr);
  return date !== null ? date : defaultDate;
}

type ExternalEventRow = {
  id: React.Key;
  eventId: ExternalEvent['eventId'];
  source: ExternalEvent;
  qualifier: ExternalEvent;
  level: ExternalEvent;
  time: ExternalEvent['timestamp'];
  more: ExternalEvent;
};

export const ExternalEventList: React.FunctionComponent<ExternalEventListProps> = ({ tourId, events }) => {
  const sortedEvents = events.sort((a, b) => {
    return compareAsc(parseDateWithDefault(a.timestamp, new Date(0)), parseDateWithDefault(b.timestamp, new Date(0)));
  });

  const isAdmin = useIsAdmin();
  const notify = useNotifications();
  const { pending, confirm, proceed, cancel } = useConfirmation();

  const [deleteTourExternalEvent] = useMutation<
    DeleteExternalTourEventMutation,
    DeleteExternalTourEventMutationVariables
  >(deleteExternalTourEventMutation);

  const [deleteStopExternalEvent] = useMutation<
    DeleteExternalStopEventMutation,
    DeleteExternalStopEventMutationVariables
  >(deleteExternalStopEventMutation);

  const [deleteDeliveryExternalEvent] = useMutation<
    DeleteExternalDeliveryEventMutation,
    DeleteExternalDeliveryEventMutationVariables
  >(deleteExternalDeliveryEventMutation);

  const onDeleteEvent = async (
    input: ExternalEvent,
    deleteTourExternalEventFn: MutationFunction<
      DeleteExternalTourEventMutation,
      DeleteExternalTourEventMutationVariables
    >,
    deleteStopExternalEventFn: MutationFunction<
      DeleteExternalStopEventMutation,
      DeleteExternalStopEventMutationVariables
    >,
    deleteDeliveryExternalEventFn: MutationFunction<
      DeleteExternalDeliveryEventMutation,
      DeleteExternalDeliveryEventMutationVariables
    >,
  ) => {
    const confirmed = await confirm();
    if (!confirmed) {
      return;
    }

    try {
      if (isTourExternalEvent(input)) {
        await deleteTourExternalEventFn({
          variables: {
            eventId: input.eventId,
          },
          refetchQueries: [{ query: tourQuery, variables: { tour_id: tourId } }],
          awaitRefetchQueries: true,
        });
      } else if (isTourExternalStopEvent(input)) {
        await deleteStopExternalEventFn({
          variables: {
            eventId: input.eventId,
          },
          refetchQueries: [{ query: tourQuery, variables: { tour_id: tourId } }],
          awaitRefetchQueries: true,
        });
      } else if (isTourExternalDeliveryEvent(input)) {
        await deleteDeliveryExternalEventFn({
          variables: {
            eventId: input.eventId,
          },
          refetchQueries: [{ query: tourQuery, variables: { tour_id: tourId } }],
          awaitRefetchQueries: true,
        });
      }

      notify.success({
        title: 'Deleted external event',
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to delete external event',
      });
    }
  };

  const columns: TableColumn<ExternalEventRow>[] = [
    {
      id: 'eventId',
      title: 'Id',
      width: '9%',
    },
    {
      id: 'source',
      title: 'Source',
      width: '18%',
      cellContent: ({ row }) => {
        const event = row.source;
        return (
          <Stack direction="column">
            <span>{breakWordAtChar(event.eventSource.toLowerCase(), '_')}</span>
            <small>
              {event.externalEventSource !== null && <span>{event.externalEventSource} </span>}
              {event.sentByCompanyId !== null && (
                <NavLink to={Routes.Company.generatePath({ company_id: event.sentByCompanyId })}>
                  #{event.sentByCompanyId}
                </NavLink>
              )}
            </small>
          </Stack>
        );
      },
    },
    {
      id: 'qualifier',
      title: 'Qualifier',
      width: '20%',
      cellContent: ({ row }) => {
        const event = row.qualifier;
        return (
          <Stack direction="column">
            <span>{event.qualifier.toLowerCase()}</span>
            <small>{breakWordAtChar(event.externalEventQualifier, '.')}</small>
          </Stack>
        );
      },
    },
    {
      id: 'level',
      title: 'Level',
      width: '16%',
      cellContent: ({ row }) => {
        const event = row.level;
        return isTourExternalStopEvent(event) ? (
          <Stack direction="column">
            <i>#{event.stopId}</i>
            <small>Stop</small>
          </Stack>
        ) : isTourExternalDeliveryEvent(event) ? (
          <Stack direction="column">
            <i>#{event.deliveryId}</i>
            <small>Delivery</small>
          </Stack>
        ) : isTourExternalEvent(event) ? (
          'Tour'
        ) : (
          '-'
        );
      },
    },
    {
      id: 'time',
      title: 'Time',
      width: '12%',
      cellContent: ({ row }) => <TimestampLink timestamp={row.time} />,
    },
    {
      id: 'more',
      title: 'More',
      width: '8%',
      cellContent: ({ row }) => {
        const event = row.more;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Tooltip
                content={
                  <>
                    {isTourExternalEvent(event) && (
                      <>
                        <div>Licence plate number: {event.licensePlateNumber ?? '-'}</div>
                        <div>Remote id: {event.remoteId ?? '-'}</div>
                        <div>Vehicle type: {notNil(event.vehicleType) ? event.vehicleType : '-'}</div>
                        <div>Location name: {event.locationName ?? '-'}</div>
                      </>
                    )}
                    <div>
                      Created at: <FormattedDate date={event.createdAt} />
                    </div>
                  </>
                }>
                <span style={{ display: 'inline-flex' }}>
                  <Icon name="small-info" />
                </span>
              </Tooltip>
              {isAdmin &&
                (isTourExternalEvent(event) ||
                  isTourExternalStopEvent(event) ||
                  isTourExternalDeliveryEvent(event)) && (
                  <Button
                    kind="subtle"
                    style={{ marginTop: '2px' }}
                    size="xsmall"
                    onClick={() =>
                      onDeleteEvent(
                        event,
                        deleteTourExternalEvent,
                        deleteStopExternalEvent,
                        deleteDeliveryExternalEvent,
                      )
                    }>
                    <Icon name="small-bin" color="#db2828" />
                  </Button>
                )}
            </span>
            {isAdmin &&
              (isTourExternalEvent(event) || isTourExternalStopEvent(event) || isTourExternalDeliveryEvent(event)) && (
                <AlertModal title="Are you sure?" description={null} trigger={null} open={pending} onClose={cancel}>
                  <Button kind="subtle" onClick={cancel}>
                    Cancel
                  </Button>
                  <Button kind="danger" onClick={proceed}>
                    Confirm
                  </Button>
                </AlertModal>
              )}
          </span>
        );
      },
    },
  ];

  const data: ExternalEventRow[] = sortedEvents.map((event, idx) => ({
    id: idx,
    eventId: event.eventId,
    source: event,
    qualifier: event,
    level: event,
    time: event.timestamp,
    more: event,
  }));

  return (
    <>
      <h3>External events</h3>
      {events.length === 0 ? (
        <p>No external events</p>
      ) : (
        <Table data={data} columns={columns} className="compact-table external-events-table" />
      )}
    </>
  );
};
